import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Package, Sparkles, Home, ShoppingBag, FileText, Calculator, Phone, Search, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', path: '/', icon: Home },
    { label: 'Products', path: '/products', icon: ShoppingBag },
    { label: 'Blogs', path: '/blogs', icon: FileText },
    { label: 'E-Bill', path: '/e-bill', icon: Calculator },
    { label: 'Contact', path: '/contact', icon: Phone }
  ];

  return (
    <>
      {/* Desktop Navigation - Top */}
      <nav className="hidden md:block fixed top-0 left-0 right-0 z-50 h-16 overflow-hidden">
        {/* Diagonal Split Background */}
        <div className="absolute inset-0">
          {/* Coral/Red section */}
          <div className="absolute inset-0 bg-gradient-to-br from-red-400 via-pink-400 to-orange-400 dark:from-red-600 dark:via-pink-600 dark:to-orange-600 transition-colors duration-300"></div>
          {/* Black diagonal section */}
          <div 
            className="absolute inset-0 bg-gray-900 dark:bg-gray-800 transition-colors duration-300"
            style={{
              clipPath: 'polygon(0 0, 65% 0, 45% 100%, 0% 100%)'
            }}
          ></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 xl:px-8 h-full">
          <div className="flex justify-between items-center h-full">
            {/* Logo Section */}
            <Link 
              to="/" 
              className="flex items-center space-x-3 group relative z-20"
            >
              <div className="flex items-center space-x-2">
                <img 
                  src="/images/logo.png" 
                  alt="Happy Steels Logo" 
                  className="h-8 w-8 object-contain"
                />
                <h1 className="text-xl font-bold text-white drop-shadow-sm">
                  Happy Steels
                </h1>
              </div>
            </Link>

            {/* Center Navigation */}
            <div className="flex items-center space-x-6">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 ${
                      isActive 
                        ? 'text-white' 
                        : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    {item.label}
                    {isActive && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white rounded-full"></div>
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Right Section - Search & Theme Toggle */}
            <div className="flex items-center space-x-4 relative z-20">
              {/* Search Bar */}
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search"
                  className="w-64 pl-10 pr-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-sm text-gray-700 placeholder-gray-500 border border-gray-200/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent transition-all duration-300"
                />
              </div>
              
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/30"
                aria-label="Toggle theme"
              >
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation - Bottom */}
      <nav className="md:hidden fixed bottom-4 left-4 right-4 z-50 bg-gray-900/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-700/50 dark:border-gray-600/50 transition-colors duration-300">
        <div className="relative px-2 py-2">
          <div className="flex justify-around items-center">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const IconComponent = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className="flex flex-col items-center justify-center relative transition-all duration-300"
                >
                  {/* Active pill background */}
                  {isActive && (
                    <div className="absolute -inset-2 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full shadow-lg shadow-orange-500/30"></div>
                  )}
                  
                  <div className="relative z-10 flex flex-col items-center">
                    <IconComponent 
                      className={`h-5 w-5 mb-0.5 transition-all duration-300 ${
                        isActive 
                          ? 'text-white scale-110' 
                          : 'text-gray-400 hover:text-white'
                      }`} 
                    />
                    <span className={`text-xs font-medium transition-all duration-300 ${
                      isActive 
                        ? 'text-white' 
                        : 'text-gray-400'
                    }`}>
                      {item.label}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Mobile Top Bar - Simple Logo Only */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white/98 dark:bg-gray-900/98 backdrop-blur-lg border-b border-gray-300/60 dark:border-gray-700/60 shadow-sm transition-colors duration-300">
        <div className="flex justify-between items-center h-12 sm:h-14 px-4">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="/images/logo.png" 
              alt="Happy Steels Logo" 
              className="h-5 w-5 sm:h-6 sm:w-6 object-contain"
            />
            <h1 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white transition-colors duration-300">
              Happy Steels
            </h1>
          </Link>
          
          {/* Mobile Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            aria-label="Toggle theme"
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </>
  );
};

export default Navbar;
